import { CommonModule } from '@angular/common';
import { Component, Signal, ViewChild, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClarityModule, ClrForm } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { FileObject } from 'src/app/services/base64/base64.service';
import { Row } from 'src/app/models/ui/table';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { MangelMediaApiFuncService } from 'src/app/services/MediaApiFunc/mangel-media-api-func.service';
import { rxResource } from '@angular/core/rxjs-interop';

export interface HerstellDaten {
  ID: number;
  Hersteller: string;
  Herstelldatum: string; // DATE
  Einbaufirma: string;
  Einbaudatum: string; // DATE
  Erneuerungsjahr: string; // YYYY
  Nutzungsdauer: number;
  DauerGewaehrleistung: number;
  EinheitGewaehrleistung: 'Tag' | 'Monat' | 'Jahr'; // Tag, Monat, Jahr
  EndeGewaehrleistung: string; // DATE
}


@Component({
  selector: 'app-multirueckmeldungdialog',
  templateUrl: './multirueckmeldungdialog.component.html',
  styleUrls: ['./multirueckmeldungdialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PreviewThumbnailsComponent,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
})
export class MultirueckmeldungdialogComponent {
  private readonly apiservice = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  protected readonly media = inject(MangelMediaApiFuncService);
  private readonly authService = inject(AuthService);
  protected localeDate = inject(BackendLocaleDatePipe);
  private readonly overlayService = inject(OverlayService);
  private readonly spinner = inject(SpinnerService);
  protected readonly selectedMangel = signal<any>(undefined);

  public readonly maengel = input<any>(undefined);
  public readonly auftrag = input<any>(undefined);
  @ViewChild(ClrForm, { static: false })
  private readonly clrForm: ClrForm;

  verantwortlicher: Array<any>;
  kostenartmangel: Array<any>;
  trial:boolean = false;
  protected readonly firmenId = signal<number>(undefined);

  
  mangelTablrueck = {
    rows: null as Row[],
    columns: [],
  };

  TEMem : TErneuerungMemory[] = [];
  
  rueckmeldungForm = new UntypedFormGroup({
    datum: new UntypedFormControl(BackendLocaleDatePipe.now, [Validators.required]),
    rueckmeldetext: new UntypedFormControl(''),
    teilerneuerung: new UntypedFormControl(''),
    verantwortlich: new UntypedFormControl(''),
    kostenart: new UntypedFormControl(''),
    bemerkung: new UntypedFormControl(''),
  });
  isopen = signal<boolean>(false);
  disableSendBtn = signal<boolean>(false);


  private readonly firmenResource = rxResource({
    params: () => this.isopen() || undefined,
    stream: () => this.apiservice.getFirmen(),
  });
  protected readonly firmen = computed(
    () =>
      this.firmenResource.value()?.rows.map(firma => ({
        ID: firma.ID,
        mFirma: `${firma.Firma}, ${firma.Abteilung}, ${firma.Ort}`,
        Firma: firma.Firma
      })) ?? []
  );
  public closedialog: BehaviorSubject<boolean> = new BehaviorSubject(false);
  dataRefresh: Subject<void>;
  @ViewChild(PreviewThumbnailsComponent, { static: false })
  preview: PreviewThumbnailsComponent;

  constructor() {
    this.isopen.set(true);

    effect(() => {
      if (!this.maengel()) return;
      untracked(() => {
        if (this.maengel()?.rows?.length) {
          if (this.mangelTablrueck.columns?.length == 0) {
            this.mangelTablrueck.columns = this.maengel().columns;
          }
          this.mangelTablrueck.rows = this.maengel().rows;
          this.selectedMangel.set(this.mangelTablrueck.rows[0]);
        }
      })
    });

    effect(() => this.firmenId.set(this.firmen().find(firma => firma.ID == this.auftrag()?.Firma)?.ID))
    effect(() => this.disableSendBtn.set(this.trial || this.auftrag() ? this.auftrag()?.Auftragsstatus != 0 : false))
    this.trial = this.authService.getToken().trial;



    this.loadFirmen();
  }

  private async loadFirmen() {
    const firmen = await firstValueFrom(this.apiservice.getFirmen());
    //this.firmen = firmen.rows;


    ({ kostenart: this.kostenartmangel, verantwortlicher: this.verantwortlicher } = await firstValueFrom(
      this.apiservice.getAuftragDetailWerte()
    ));
  }


  
  async setSelectedMangel(event) {
    let prevID = this.selectedMangel()?.omazsid ?? undefined
    this.selectedMangel.set(event);
    let fe = this.TEMem.find(x=>x.ID == prevID);
    if (!fe) 
      this.TEMem.push(
        {
          ID: prevID ?? event.omazsid,
          bemerkung: this.rueckmeldungForm.value.bemerkung,
          teilerneuerung: this.rueckmeldungForm.value.teilerneuerung,
          Bild: await this.preview.packUnsavedFiles()
        }
      );
    fe = this.TEMem.find(x=>x.ID == event.omazsid);
    if(fe) {
      this.rueckmeldungForm.controls.teilerneuerung.setValue(fe.teilerneuerung);
      this.rueckmeldungForm.controls.bemerkung.setValue(fe.bemerkung);
      this.preview.unpackFiles(fe.Bild);
    }    
    else {
      this.rueckmeldungForm.controls.teilerneuerung.setValue('');
      this.rueckmeldungForm.controls.bemerkung.setValue('');
      this.preview.clearPreview();
    }
      
  }
  leaveTE(event, field:string) {
    let fe = this.TEMem.find(x=>x.ID == this.selectedMangel()?.omazsid);
    if(fe)fe[field] = this.rueckmeldungForm.value[field];
  }
  async leaveBild() {
    let fe = this.TEMem.find(x=>x.ID == this.selectedMangel()?.omazsid);
    if(fe)fe.Bild = await this.preview.packAllFiles();
  }
  
   async sendRueckmeldung() {
    this.spinner.enable();

    let ihbilder = await this.preview.packUnsavedFiles();
    const formValues = structuredClone(this.rueckmeldungForm.value);
    const date = formValues.datum;
    delete formValues.datum;
    const maengel = this.mangelTablrueck.rows.map(row => {
      return {
        ...formValues,
        omazsid: row.omazsid,
        date: date,
        instandsetzer: this.firmenId(),
        ...this.TEMem.find(x=> x.ID == row.omazsid)
      };
    });
    this.apiservice
      .sendMultiRueckmeldung({ maengel: maengel, bilder: ihbilder })
      .pipe(first())
      .subscribe(({ success, message }: any) => {
        if (success) {
          message = this.mrTranslate.transform('Rückmeldung gespeichert');
          //this.auftrag = { ...this.auftrag, Auftragsstatus: 1 };
          this.rueckmeldungForm.disable();
          this.dataRefresh?.next();
          this.toastr.success(message);
        } else this.toastr.error(message);
        this.spinner.disable();
      });
  }


  protected openPdf(file: File) {
    this.overlayService.setOverlay({
      overlay: 'viewAuftrag',
      isTopOverlay: true,
      data: {
        data: file,
        titel: file?.name || this.mrTranslate.transform('Datei'),
        file: file?.name + '.pdf',
      },
      recallData: {
        status: this.auftrag().Auftragsstatus,
        preview: this.preview?.rawPreviewThumbnails,
      },
      zIndex: 5,
    });
  }
  public close() {
    this.closedialog.next(true);
  }
}

interface TErneuerungMemory {
  ID: number;
  teilerneuerung: string;
  bemerkung: string;
  Bild?: FileObject[];
}

